// host.ts — host dashboard at /host.
//
// Browser login (username + password, password-manager-friendly) → a streamer
// JWT kept in localStorage. Shows the session history (live now + recent), each
// clickable to open the listener view, plus the long-lived PLUGIN connect token
// to paste into the plugin once. Streamer-only (every call is Bearer-authed).

import {
  login,
  createHostSession,
  activateHostSession,
  getHostSessions,
  deleteHostSession,
  getHostDevices,
  approveDevice,
  revokeDevice,
  ApiError,
  type HostSession,
  type HostDevice,
} from "./api";
import { BRAND_LOGO_URL } from "./ui";

const TOKEN_KEY = "daws:host:token";
const REFRESH_MS = 5000;

export class HostDashboard {
  private readonly root: HTMLElement;
  private token: string | null;
  private timer: number | null = null;

  constructor(root: HTMLElement) {
    this.root = root;
    this.token = localStorage.getItem(TOKEN_KEY);
  }

  async start(): Promise<void> {
    this.root.dataset.state = "host";
    document.title = "DAW Stream — Host";
    if (this.token) {
      try {
        const sessions = await getHostSessions(this.token);
        this.renderDashboard(sessions);
        return;
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          this.clearToken();
        }
      }
    }
    this.renderLogin();
  }

  private clearToken(): void {
    this.token = null;
    localStorage.removeItem(TOKEN_KEY);
  }

  private header(): HTMLElement {
    const h = document.createElement("header");
    h.className = "app__header";
    h.innerHTML = `<div class="app__logo"><img src="${BRAND_LOGO_URL}" alt="Julius Gass"></div><div class="app__title">DAW Stream — Host</div>`;
    return h;
  }

  // -------------------------------------------------------------------------
  // Login
  // -------------------------------------------------------------------------

  private renderLogin(errMsg?: string): void {
    this.stopRefresh();
    this.root.innerHTML = "";
    this.root.appendChild(this.header());

    const card = document.createElement("main");
    card.className = "card";
    card.innerHTML = `<h1 class="card__title">Host sign-in</h1><p class="card__sub">Open a session to control music, calls, participants and feedback in one place.</p>`;

    const form = document.createElement("form");
    form.className = "form";
    form.noValidate = true;

    const user = field("Username", "text", "juliusgass");
    user.input.value = "juliusgass";
    user.input.autocomplete = "username";
    const pass = field("Password", "password", "Password");
    pass.input.autocomplete = "current-password";

    const err = document.createElement("p");
    err.className = "form__error";
    if (errMsg) err.textContent = errMsg;
    else err.hidden = true;

    const btn = document.createElement("button");
    btn.type = "submit";
    btn.className = "btn btn--primary";
    btn.textContent = "Sign in";

    form.append(user.wrap, pass.wrap, err, btn);
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      const u = user.input.value.trim();
      const p = pass.input.value;
      if (!u || !p) {
        err.hidden = false;
        err.textContent = "Username and password required.";
        return;
      }
      btn.disabled = true;
      btn.textContent = "…";
      void login(u, p)
        .then((r) => {
          this.token = r.token;
          localStorage.setItem(TOKEN_KEY, r.token);
          return getHostSessions(r.token);
        })
        .then((sessions) => this.renderDashboard(sessions))
        .catch((e2) => {
          this.clearToken();
          this.renderLogin(
            e2 instanceof ApiError ? e2.message || "Sign-in failed." : "Sign-in failed.",
          );
        });
    });

    card.appendChild(form);
    this.root.appendChild(card);
    (errMsg ? pass.input : user.input).focus();
  }

  // -------------------------------------------------------------------------
  // Dashboard
  // -------------------------------------------------------------------------

  private renderDashboard(sessions: HostSession[]): void {
    this.root.innerHTML = "";
    this.root.appendChild(this.header());

    const main = document.createElement("main");
    main.className = "host-dash";

    // --- plugin token + logout row ---
    const bar = document.createElement("div");
    bar.className = "host-dash__bar";
    // REVIEW 4.1: the "Copy plugin token" fallback is gone with its route — it
    // handed out an unrevokable ~10-year credential (and dropped it into a
    // window.prompt in plain text when the clipboard was blocked). Devices are
    // authorized below: Connect in the plugin, approve here.
    const logout = document.createElement("button");
    logout.type = "button";
    logout.className = "btn btn--ghost";
    logout.textContent = "Sign out";
    logout.addEventListener("click", () => {
      this.clearToken();
      this.renderLogin();
    });
    bar.append(logout);
    main.appendChild(bar);
    const create = document.createElement("form"); create.className = "host-create";
    const sessionName = field("New session", "text", "e.g. song-mix");
    sessionName.input.required = true; sessionName.input.pattern = "[a-z0-9]+(?:-[a-z0-9]+)*";
    sessionName.input.maxLength = 64;
    const createButton = document.createElement("button"); createButton.type = "submit";
    createButton.className = "btn btn--primary"; createButton.textContent = "Create session";
    const createError = document.createElement("p"); createError.className = "form__error"; createError.setAttribute("role", "alert");
    create.append(sessionName.wrap, createButton, createError);
    create.addEventListener("submit", e => {
      e.preventDefault(); if (!this.token) return;
      createButton.disabled = true;
      void getHostSessions(this.token).then(existing => {
        const id = sessionName.input.value.trim();
        if (existing.some(s => s.id === id)) throw new Error("This session already exists. Use Open controls below.");
        return createHostSession(id, this.token!);
      }).then(s => {
        location.assign(`/?s=${encodeURIComponent(s.sessionId)}&k=${encodeURIComponent(s.linkKey)}&host=1`);
      }).catch(e => { createError.textContent = e instanceof Error ? e.message : "Could not create session."; createButton.disabled = false; });
    });
    main.append(create);


    // --- §10 devices (approve pending, revoke approved) ---
    const devWrap = document.createElement("div");
    devWrap.className = "host-dash__devices";
    main.appendChild(devWrap);
    void this.refreshDevices();

    const live = sessions.filter((s) => s.live);
    const recent = sessions.filter((s) => !s.live);

    main.appendChild(this.sectionEl("Live now", live, "No live session right now."));
    main.appendChild(this.sectionEl("Recent", recent, "No past sessions yet."));

    this.root.appendChild(main);

    // Live-refresh the lists (token panel/buttons are rebuilt too — fine).
    this.startRefresh();
  }

  /** Fetch + render the §10 device list into the .host-dash__devices container. */
  private async refreshDevices(): Promise<void> {
    if (!this.token) return;
    const wrap = this.root.querySelector<HTMLElement>(".host-dash__devices");
    if (!wrap) return;
    let devices: HostDevice[] = [];
    try {
      devices = await getHostDevices(this.token);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        this.clearToken();
        this.renderLogin();
      }
      return;
    }
    const pending = devices.filter((d) => d.status === "pending");
    const approved = devices.filter((d) => d.status === "approved");

    wrap.innerHTML = "";
    const sec = document.createElement("section");
    sec.className = "host-dash__section";
    const h = document.createElement("h2");
    h.className = "host-dash__heading";
    h.textContent = `Devices (${approved.length} approved${pending.length ? `, ${pending.length} pending` : ""})`;
    sec.appendChild(h);

    if (pending.length === 0 && approved.length === 0) {
      const p = document.createElement("p");
      p.className = "host-dash__empty";
      p.textContent = "No devices yet. Click \u201eConnect\u201c in the plugin and it will show up here for approval.";
      sec.appendChild(p);
    } else {
      const ul = document.createElement("ul");
      ul.className = "host-dash__list";
      // Pending first (they need action), highlighted with the code.
      for (const d of pending) ul.appendChild(this.deviceRow(d, "pending"));
      for (const d of approved) ul.appendChild(this.deviceRow(d, "approved"));
      sec.appendChild(ul);
    }
    wrap.appendChild(sec);
  }

  private deviceRow(d: HostDevice, kind: "pending" | "approved"): HTMLElement {
    const li = document.createElement("li");
    li.className = "host-dash__item";

    const name = document.createElement("span");
    name.className = "host-dash__name";
    name.textContent = d.name;

    const meta = document.createElement("span");
    meta.className = "host-dash__meta";
    meta.textContent =
      kind === "pending"
        ? `Code ${d.userCode}`
        : `approved ${relTime(d.approvedMs)}`;

    const btn = document.createElement("button");
    btn.type = "button";
    if (kind === "pending") {
      btn.className = "btn btn--primary host-dash__open";
      btn.textContent = `Approve (${d.userCode})`;
      btn.title = "Approve only if this code matches the one shown in the plugin.";
      btn.addEventListener("click", () => {
        if (!this.token) return;
        btn.disabled = true;
        btn.textContent = "…";
        void approveDevice(d.deviceId, this.token)
          .then(() => this.refreshDevices())
          .catch((e) => {
            btn.disabled = false;
            btn.textContent = `Error: ${e instanceof ApiError ? e.message : "retry"}`;
          });
      });
    } else {
      btn.className = "btn btn--ghost host-dash__open";
      btn.textContent = "Revoke";
      btn.title = "Disconnects this device immediately and revokes its access. The device can re-connect any time - it just needs a fresh approval.";
      btn.addEventListener("click", () => {
        if (!this.token) return;
        if (!window.confirm(`Revoke access for \u201e${d.name}\u201c?`)) return;
        btn.disabled = true;
        void revokeDevice(d.deviceId, this.token)
          .then(() => this.refreshDevices())
          .catch(() => {
            btn.disabled = false;
            btn.textContent = "Error - retry";
          });
      });
    }

    li.append(name, meta, btn);
    return li;
  }

  private sectionEl(title: string, items: HostSession[], emptyMsg: string): HTMLElement {
    const sec = document.createElement("section");
    sec.className = "host-dash__section";
    const h = document.createElement("h2");
    h.className = "host-dash__heading";
    h.textContent = `${title} (${items.length})`;
    sec.appendChild(h);

    if (items.length === 0) {
      const p = document.createElement("p");
      p.className = "host-dash__empty";
      p.textContent = emptyMsg;
      sec.appendChild(p);
      return sec;
    }

    const ul = document.createElement("ul");
    ul.className = "host-dash__list";
    for (const s of items) {
      const li = document.createElement("li");
      li.className = "host-dash__item";
      li.dataset.live = s.live ? "1" : "0";

      const dot = document.createElement("span");
      dot.className = "host-dash__dot";
      const name = document.createElement("span");
      name.className = "host-dash__name";
      name.textContent = s.id; // id doubles as the name
      const meta = document.createElement("span");
      meta.className = "host-dash__meta";
      meta.textContent = s.live ? "live" : `last live ${relTime(s.lastLiveMs)}`;

      const open = document.createElement("button");
      open.type = "button";
      open.className = "btn btn--primary host-dash__open";
      open.textContent = "Open controls";
      // The link key is MANDATORY on the listener page since §16 — without it
      // the server answers like the session does not exist (by design), which
      // used to strand the host on "Not streaming yet".
      const url = `/?s=${encodeURIComponent(s.id)}${s.linkKey ? `&k=${encodeURIComponent(s.linkKey)}` : ""}`;
      open.title = `https://${location.host}${url}`;
      open.addEventListener("click", () => {
        if (!this.token) return;
        open.disabled = true;
        void activateHostSession(s.id, this.token).then(session => {
          location.assign(`/?s=${encodeURIComponent(session.sessionId)}&k=${encodeURIComponent(session.linkKey)}&host=1`);
        }).catch(() => { open.disabled = false; open.textContent = "Could not open · retry"; });
      });

      li.append(dot, name, meta, open);

      // Cleanup: old sessions can be deleted (live ones are server-guarded too).
      if (!s.live) {
        const del = document.createElement("button");
        del.type = "button";
        del.className = "btn btn--ghost host-dash__open";
        del.textContent = "Delete";
        del.title = "Remove this session (its link goes dead until you stream the same id again)";
        del.addEventListener("click", () => {
          if (!this.token) return;
          if (!window.confirm(`Delete session \u201e${s.id}\u201c?`)) return;
          del.disabled = true;
          deleteHostSession(s.id, this.token)
            .then(() => getHostSessions(this.token!))
            .then((sessions) => this.refreshLists(sessions))
            .catch((e) => {
              del.disabled = false;
              window.alert(`Delete failed: ${(e as Error).message}`);
            });
        });
        li.appendChild(del);
      }
      ul.appendChild(li);
    }
    sec.appendChild(ul);
    return sec;
  }


  private startRefresh(): void {
    this.stopRefresh();
    this.timer = window.setInterval(() => {
      if (!this.token) return;
      void getHostSessions(this.token)
        .then((sessions) => this.refreshLists(sessions))
        .catch((e) => {
          if (e instanceof ApiError && e.status === 401) {
            this.clearToken();
            this.renderLogin();
          }
        });
      // §10: keep the device list current so a pending device appears promptly.
      void this.refreshDevices();
    }, REFRESH_MS);
  }

  private stopRefresh(): void {
    if (this.timer !== null) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  /** Re-render only the two list sections in place (keep the token/logout bar). */
  private refreshLists(sessions: HostSession[]): void {
    const main = this.root.querySelector(".host-dash");
    if (!main) return;
    // Direct children only: the Devices section is NESTED inside
    // .host-dash__devices and the old selector wiped it on every refresh.
    main.querySelectorAll(":scope > .host-dash__section").forEach((el) => el.remove());
    const live = sessions.filter((s) => s.live);
    const recent = sessions.filter((s) => !s.live);
    main.appendChild(this.sectionEl("Live now", live, "No live session right now."));
    main.appendChild(this.sectionEl("Recent", recent, "No past sessions yet."));
  }
}

// --- small helpers ---------------------------------------------------------

function field(label: string, type: string, placeholder: string) {
  const wrap = document.createElement("label");
  wrap.className = "field";
  const span = document.createElement("span");
  span.className = "field__label";
  span.textContent = label;
  const input = document.createElement("input");
  input.type = type;
  input.className = "field__input";
  input.placeholder = placeholder;
  wrap.append(span, input);
  return { wrap, input };
}

function relTime(ms: number): string {
  const d = Date.now() - ms;
  if (!(d >= 0)) return "just now";
  const m = Math.floor(d / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h ago`;
  const days = Math.floor(h / 24);
  return `${days} d ago`;
}
